import {
  useRef,
  useState,
  ChangeEvent,
  useEffect,
  useCallback,
  useMemo,
} from 'react';
import {
  Box,
  Button,
  Flex,
  Heading,
  Text,
  FormControl,
  FormErrorMessage,
  Input,
  InputGroup,
  InputLeftElement,
  InputRightElement,
  Tooltip,
  CloseButton,
  FormErrorIcon,
  useToast,
  Stack,
  Image,
  Link,
  CircularProgress,
} from '@chakra-ui/react';
import { AuditLogsTable } from './AuditLogsTable';
import { PortalIcon } from 'features/common/components/PortalIcon';
import { mdiMagnify } from '@mdi/js';
import {
  AuditLogsSupportedSystemName,
  SYSTEM_DISPLAY_NAMES,
} from './constants';
import EmptyState from '../common/components/EmptyState';
import { RepeatIcon } from '@chakra-ui/icons';
import { useGetSystems } from './hooks/useGetSystems';
import { AuditLogsSystem } from './types';
import { SystemSelector, TenantSelector } from './Selectors';
import { withErrorBoundary } from 'features/common/utils/errorBoundaries';
import { useGetUserOrgDepsQuery } from 'gql/graphql';

const filterSystemsApiData = <T extends { system: string }>(
  apiData: T[],
  shouldShowOnlyIdentityLogs: boolean,
): T[] => {
  if (!shouldShowOnlyIdentityLogs) {
    return apiData;
  }

  // For non-internal orgs, only include Identity system data
  return apiData.filter((item) => item.system === 'Identity');
};

function useDebounce(value: string): string {
  const [debounced, setDebounced] = useState(value);

  useEffect(() => {
    if (process.env.NODE_ENV === 'test') {
      setDebounced(value);
      return;
    }

    const timer = setTimeout(() => setDebounced(value), 400);
    return () => clearTimeout(timer);
  }, [value]);

  return debounced;
}

export const AuditLogs = () => {
  const toast = useToast();

  const [selectedSystemId, setSelectedSystemId] =
    useState<AuditLogsSupportedSystemName>();
  const [searchFilter, setSearchFilter] = useState('');
  const [searchStringIsValid, setSearchStringIsValid] = useState(true);
  const [selectedTenantId, setSelectedTenantId] = useState('');
  const [hasRetried, setHasRetried] = useState(false);
  const [hasLogsError, setHasLogsError] = useState(false);

  const tableRef = useRef<{ reloadData: () => void }>(null);

  const {
    data: orgData,
    loading: isOrgLoading,
    error: orgError,
    refetch: refetchOrg,
  } = useGetUserOrgDepsQuery();

  const currentUserOrg = orgData?.organization ?? undefined;
  const shouldShowOnlyIdentityLogs =
    currentUserOrg && currentUserOrg.type !== 'INTERNAL';

  const onError = useCallback(() => {
    toast({
      description: 'Failed to load available products. Please try again.',
      title: 'Error',
      status: 'error',
      duration: 5000,
      isClosable: true,
    });
  }, [toast]);

  const {
    data: systems,
    isLoading: isSystemsLoading,
    isError: isSystemsError,
    refetch: refetchSystems,
    error: systemsError,
  } = useGetSystems({
    onError,
  });

  const isLoading = isSystemsLoading || isOrgLoading;
  const hasError = isSystemsError || !!orgError;
  const combinedError = systemsError || orgError;

  const handleRetry = () => {
    setHasRetried(true);
    refetchSystems();
    refetchOrg();
  };

  const getErrorDetails = () => {
    if (!combinedError || typeof combinedError !== 'object') return undefined;

    // Handle REST API errors (from systems API)
    if (systemsError) {
      const err = systemsError as {
        response?: { status?: number; data?: { detail?: string } };
      };
      return {
        status: err.response?.status,
        detail: err.response?.data?.detail,
      };
    }

    // Handle GraphQL errors (from org API) - fallback to simple message
    if (orgError) {
      return {
        status: undefined,
        detail: 'Unable to load organization information. Please try again.',
      };
    }

    return undefined;
  };

  const { systemsList, tenantsBySystem } = useMemo(() => {
    const uniqueSystems = new Set<string>();
    const activeTenantsMap: Record<string, { value: string; label: string }[]> =
      {};
    const deletedTenantsMap: Record<
      string,
      { value: string; label: string }[]
    > = {};
    const rawApiData = systems?.data || [];

    // Filter API data based on organization type
    const apiData = filterSystemsApiData(
      rawApiData,
      shouldShowOnlyIdentityLogs ?? false,
    );

    apiData.forEach((item: AuditLogsSystem) => {
      let systemName = item.system;

      // Handle special case for Identity system with tenantIds which are considered as Support Login
      if (
        item.system === 'Identity' &&
        item.tenantId !== 'N/A' &&
        item.tenantName !== 'N/A'
      ) {
        systemName = 'SupportLogin';
      }

      uniqueSystems.add(systemName);

      // Group tenants by system (excluding N/A values)
      if (item.tenantId !== 'N/A' && item.tenantName !== 'N/A') {
        const isDeleted = item.tenantName === 'UNKNOWN';
        const tenant = {
          value: item.tenantId,
          label: isDeleted ? `(Deleted) ${item.tenantId}` : item.tenantName,
        };

        if (isDeleted) {
          if (!deletedTenantsMap[systemName]) {
            deletedTenantsMap[systemName] = [];
          }

          deletedTenantsMap[systemName].push(tenant);
        } else {
          if (!activeTenantsMap[systemName]) {
            activeTenantsMap[systemName] = [];
          }

          activeTenantsMap[systemName].push(tenant);
        }
      } else {
        uniqueSystems.add(item.system);
      }
    });

    const sortedGrouped: Record<string, { value: string; label: string }[]> =
      {};
    uniqueSystems.forEach((system) => {
      const activeTenants = activeTenantsMap[system] || [];
      const deletedTenants = deletedTenantsMap[system] || [];

      activeTenants.sort((a, b) => a.label.localeCompare(b.label));
      deletedTenants.sort((a, b) => a.label.localeCompare(b.label));

      sortedGrouped[system] = [...activeTenants, ...deletedTenants];
    });

    const systemsList = Array.from(uniqueSystems).map((system) => ({
      value: system,
      label:
        SYSTEM_DISPLAY_NAMES[system as keyof typeof SYSTEM_DISPLAY_NAMES] ||
        system,
    }));

    return {
      systemsList,
      tenantsBySystem: sortedGrouped,
    };
  }, [systems?.data, shouldShowOnlyIdentityLogs]);

  const tenantsList = selectedSystemId
    ? tenantsBySystem[selectedSystemId] || []
    : [];

  const debouncedSearch = useDebounce(searchFilter);

  const handleSystemChange = (system: AuditLogsSupportedSystemName) => {
    setSelectedSystemId(system);
    setSelectedTenantId('');
    setHasLogsError(false);
  };

  const handleTenantChange = (tenant: string) => {
    setSelectedTenantId(tenant);
    setHasLogsError(false);
  };

  // Check if both system and tenant are selected (or if system has no tenants)
  const canShowAuditLogs =
    selectedSystemId && (selectedTenantId || tenantsList.length === 0);

  // Support Login is part of Identity system
  const getApiSystemName = (uiSystemName: string): string => {
    return uiSystemName === 'SupportLogin' ? 'Identity' : uiSystemName;
  };

  return (
    <Flex flexDir='column' height='100%'>
      <Flex justifyContent='space-between' width='100%'>
        <Flex direction='column' alignSelf='start'>
          <Heading as='h1' data-testid='auditLogs-heading'>
            Audit logs
          </Heading>
          <Text my='8' data-testid='auditLogs-description'>
            Audit logs show a trace list of activities performed in Cloud
            Portal. For more information see the{' '}
            <Link
              href='https://doc.sitecore.com/portal/en/developers/sitecore-cloud-portal/use-the-common-audit-log-interface.html'
              isExternal
            >
              documentation
            </Link>
            .
          </Text>
        </Flex>
      </Flex>
      <Flex direction='column' gap={4} mb={4} width='100%'>
        <Flex
          direction={{ base: 'column', lg: 'row' }}
          align={{ base: 'stretch', lg: 'center' }}
          gap={3}
          width='100%'
        >
          <Box
            w={{ base: '100%', lg: 'auto' }}
            minW={{ base: 'auto', lg: '200px' }}
            maxW={{ base: '100%', lg: '270px' }}
            flex={{ base: 'none', lg: '1 1 270px' }}
          >
            <SystemSelector
              systemsList={systemsList}
              onSystemChange={handleSystemChange}
              isDisabled={isLoading || hasError}
              selectedValue={selectedSystemId}
              isError={hasError}
            />
          </Box>
          <Box
            w={{ base: '100%', lg: 'auto' }}
            minW={{ base: 'auto', lg: '200px' }}
            maxW={{ base: '100%', lg: '320px' }}
            flex={{ base: 'none', lg: '1 1 320px' }}
          >
            <TenantSelector
              tenantsList={tenantsList}
              onTenantChange={handleTenantChange}
              isDisabled={
                !selectedSystemId ||
                tenantsList.length === 0 ||
                isLoading ||
                hasError
              }
              selectedValue={selectedTenantId}
              selectedSystemId={selectedSystemId}
              isLoading={isLoading}
              isError={hasError}
            />
          </Box>
          <Box
            w={{ base: '100%', lg: 'auto' }}
            minW={{ base: 'auto', lg: '200px' }}
            maxW={{ base: '100%', lg: '320px' }}
            flex={{ base: 'none', lg: '1.2 1 340px' }}
          >
            <FormControl isInvalid={!searchStringIsValid}>
              <InputGroup w='100%'>
                <InputLeftElement pointerEvents='none'>
                  <PortalIcon path={mdiMagnify} />
                </InputLeftElement>
                <Input
                  placeholder='Search logs'
                  value={searchFilter}
                  onChange={(e: ChangeEvent<HTMLInputElement>) => {
                    const val = e.target.value;
                    if (/^[\w@.-]*$/.test(val)) {
                      setSearchStringIsValid(true);
                      setSearchFilter(val);
                    } else {
                      setSearchStringIsValid(false);
                    }
                  }}
                  width='100%'
                  role='search'
                />
                {searchFilter && (
                  <InputRightElement>
                    <Tooltip label='Clear'>
                      <CloseButton
                        aria-label='Clear'
                        onClick={() => setSearchFilter('')}
                      />
                    </Tooltip>
                  </InputRightElement>
                )}
              </InputGroup>
              <FormErrorMessage
                visibility={searchStringIsValid ? 'hidden' : 'visible'}
                minH='1.25rem'
              >
                <FormErrorIcon />
                Currently, the search is only available in alphanumeric
                characters, @, ., -, _.
              </FormErrorMessage>
            </FormControl>
          </Box>
          {canShowAuditLogs && !hasError && !hasLogsError && (
            <Box
              ml={{ base: 0, lg: 'auto' }}
              w={{ base: '100%', lg: 'auto' }}
              flexShrink={0}
              minW={{ base: 'auto', lg: '120px' }}
            >
              <Button
                variant='outline'
                aria-label='audit-logs-reload-button'
                size='sm'
                data-testid='audit-logs-reload-button'
                leftIcon={<RepeatIcon />}
                onClick={() => tableRef?.current?.reloadData()}
                whiteSpace='nowrap'
                w={{ base: '100%', lg: 'auto' }}
              >
                Reload data
              </Button>
            </Box>
          )}
        </Flex>
      </Flex>
      {selectedSystemId && (
        <Box
          w='100%'
          textAlign='center'
          mb={4}
          data-testid='selected-system-label-wrapper'
        >
          <Text
            fontSize='sm'
            fontWeight='bold'
            color='chakra-subtle-text'
            data-testid='selected-system-label'
          >
            Selected product:{' '}
            {SYSTEM_DISPLAY_NAMES[
              selectedSystemId as keyof typeof SYSTEM_DISPLAY_NAMES
            ] || selectedSystemId}
            {selectedTenantId &&
              ` | Tenant: ${
                tenantsList.find((t) => t.value === selectedTenantId)?.label
              }`}
          </Text>
        </Box>
      )}
      {hasError ? (
        <Flex flex={1} justifyContent='center' alignItems='center'>
          <Stack
            align='center'
            textAlign='center'
            spacing='6'
            maxW='sm'
            data-testid='audit-logs-error-state'
          >
            <Image
              boxSize='32'
              src='https://delivery-sitecore.sitecorecontenthub.cloud/api/public/content/spot-alert-circle'
              alt='error'
            />
            <Stack spacing='2'>
              <Heading as='h2' size='lg'>
                {hasRetried
                  ? `Retry failed with error code ${
                      getErrorDetails()?.status || 'Unknown'
                    }`
                  : 'Failed to load products'}
              </Heading>
              <Text variant='small'>
                {hasRetried
                  ? getErrorDetails()?.detail
                    ? `${getErrorDetails()?.detail}`
                    : 'Products unavailable'
                  : 'Products unavailable'}
              </Text>
            </Stack>
            <Text maxWidth='sm'>
              {hasRetried ? (
                <>
                  If this error persists, try visiting the{' '}
                  <Link
                    mb={4}
                    href='https://support.sitecore.com/kb'
                    isExternal
                  >
                    Knowledge Base
                  </Link>{' '}
                  for assistance or reach out to Sitecore Support team.
                </>
              ) : (
                'Unable to load available products for audit logs. Please try again.'
              )}
            </Text>
            <Button variant='link' onClick={handleRetry}>
              Retry
            </Button>
          </Stack>
        </Flex>
      ) : canShowAuditLogs ? (
        <AuditLogsTable
          system={
            getApiSystemName(selectedSystemId) as AuditLogsSupportedSystemName
          }
          ref={tableRef}
          searchTerm={debouncedSearch}
          tenantId={selectedTenantId}
          onError={() => setHasLogsError(true)}
          onSuccess={() => setHasLogsError(false)}
        />
      ) : (
        <Flex flex={1} justifyContent='center' alignItems='center'>
          {isLoading ? (
            <CircularProgress
              isIndeterminate
              capIsRound
              color='primary'
              trackColor='neutral-color.200'
            />
          ) : (
            <EmptyState
              text={
                !selectedSystemId ? 'No product selected' : 'No tenant selected'
              }
              description={
                !selectedSystemId
                  ? 'Select a product to view its audit logs'
                  : 'Select a tenant to view audit logs for this product'
              }
              dataTestId='audit-logs-empty-state'
            />
          )}
        </Flex>
      )}
    </Flex>
  );
};

export const AuditLogsWithErrorBoundary = withErrorBoundary(AuditLogs);
